import type { Doc, Id } from '@bill-alps/backend/convex/_generated/dataModel';
import type { MutationCtx } from '@bill-alps/backend/convex/_generated/server';
import type { BillingModuleKey } from '@bill-alps/backend/features/companies/modules';
import type { Locale } from '@bill-alps/backend/shared/format/locales';
import {
  fiduciaireOverage,
  type FiduciaireUsageFeature,
} from '@bill-alps/backend/features/fiduciaire/plans';

/**
 * Monthly overage meters for fiduciaire-paid mandates (P3): the metered gates
 * call `recordMandateUsage` instead of throwing their quota error — usage is
 * never hard-blocked, it is billed. Everything here is a no-op unless the
 * company is stamped `billingSource: 'fiduciaire'` AND the feature's module
 * is on the mandate's billing row, so client-paid companies keep the exact
 * plan-limit behaviour they had.
 */

/** The module whose billing row must cover a metered feature. */
export const FIDUCIAIRE_USAGE_MODULE: Record<
  FiduciaireUsageFeature,
  BillingModuleKey
> = {
  payslips: 'payroll',
  aiPrefills: 'invoicing',
  supplierBillExtractions: 'accounting',
  aiCategorizations: 'accounting',
};

/** 'YYYY-MM' in UTC — the aggregation unit, aligned with the cycle anchor. */
export function usageMonthKey(now: number): string {
  const date = new Date(now);
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}`;
}

/** The month that just closed relative to `now` — what a cycle invoice bills. */
export function previousMonthKeyUtc(now: number): string {
  const date = new Date(now);
  // Day 0 of the current month is the last day of the previous one.
  return usageMonthKey(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 0));
}

type UsageCompany = Pick<Doc<'companies'>, '_id' | 'billingSource'>;

/**
 * The mandateBilling row that makes a feature overage-billed for this
 * company, or null when the feature runs on plain plan limits: not
 * fiduciaire-paid, or the feature's module is not on the billed set (an
 * uncovered module runs at free-plan limits and must keep throwing).
 */
export async function getMandateUsageBilling(
  ctx: MutationCtx,
  company: UsageCompany,
  feature: FiduciaireUsageFeature,
): Promise<Doc<'mandateBilling'> | null> {
  if (company.billingSource !== 'fiduciaire') return null;
  const row = await ctx.db
    .query('mandateBilling')
    .withIndex('by_company', (q) => q.eq('companyId', company._id))
    .first();
  if (!row || !row.modules.includes(FIDUCIAIRE_USAGE_MODULE[feature])) {
    return null;
  }
  return row;
}

/**
 * Count `count` units against the mandate's current-month meter. Returns
 * whether the feature is overage-billed here — false tells the gate to apply
 * its normal plan-limit assert instead. `count <= 0` writes nothing but still
 * answers (batch gates record their final tally, which can be zero).
 */
export async function recordMandateUsage(
  ctx: MutationCtx,
  company: UsageCompany,
  feature: FiduciaireUsageFeature,
  count = 1,
): Promise<boolean> {
  const billing = await getMandateUsageBilling(ctx, company, feature);
  if (!billing) return false;
  if (count <= 0) return true;

  const now = Date.now();
  const monthKey = usageMonthKey(now);
  const existing = await ctx.db
    .query('mandateUsage')
    .withIndex('by_company_month_feature', (q) =>
      q
        .eq('companyId', company._id)
        .eq('monthKey', monthKey)
        .eq('feature', feature),
    )
    .first();
  if (existing) {
    await ctx.db.patch(existing._id, {
      count: existing.count + count,
      updatedAt: now,
    });
  } else {
    await ctx.db.insert('mandateUsage', {
      fiduciaireId: billing.fiduciaireId,
      companyId: company._id,
      monthKey,
      feature,
      count,
      createdAt: now,
      updatedAt: now,
    });
  }
  return true;
}

/**
 * Undo a recorded unit after a failed act (mirrors the quota refunds). Keyed
 * on the meter row directly — no billing-row check, so a hand-back between
 * record and refund still refunds. Floors at zero; a missing row (never
 * recorded, other month) is a silent no-op.
 */
export async function refundMandateUsage(
  ctx: MutationCtx,
  companyId: Id<'companies'>,
  feature: FiduciaireUsageFeature,
  count = 1,
  monthKey = usageMonthKey(Date.now()),
): Promise<void> {
  if (count <= 0) return;
  const existing = await ctx.db
    .query('mandateUsage')
    .withIndex('by_company_month_feature', (q) =>
      q
        .eq('companyId', companyId)
        .eq('monthKey', monthKey)
        .eq('feature', feature),
    )
    .first();
  if (!existing || existing.count <= 0) return;
  await ctx.db.patch(existing._id, {
    count: Math.max(0, existing.count - count),
    updatedAt: Date.now(),
  });
}

// ---- Overage push (pure halves, unit-tested without Stripe) -----------------

export interface OverageLine {
  companyId: Id<'companies'>;
  feature: FiduciaireUsageFeature;
  used: number;
  included: number;
  overCount: number;
  /** CHF excl. VAT, rounded to Rappen. */
  amountChf: number;
}

/**
 * One month of meters → the billable lines: the allowance applied per
 * (mandate, feature), zero-overage pairs dropped. Deterministic order so a
 * webhook retry rebuilds the exact same idempotency keys.
 */
export function computeOverageLines(
  rows: Array<Pick<Doc<'mandateUsage'>, 'companyId' | 'feature' | 'count'>>,
): OverageLine[] {
  const used = new Map<string, OverageLine>();
  for (const row of rows) {
    const key = `${row.companyId}:${row.feature}`;
    const entry = used.get(key);
    if (entry) entry.used += row.count;
    else {
      used.set(key, {
        companyId: row.companyId,
        feature: row.feature,
        used: row.count,
        included: 0,
        overCount: 0,
        amountChf: 0,
      });
    }
  }
  const lines: OverageLine[] = [];
  for (const line of used.values()) {
    const overage = fiduciaireOverage(line.feature, line.used);
    if (overage.overCount <= 0 || overage.amountChf <= 0) continue;
    lines.push({
      ...line,
      included: overage.included,
      overCount: overage.overCount,
      amountChf: overage.amountChf,
    });
  }
  return lines.sort((a, b) =>
    a.companyId === b.companyId
      ? a.feature.localeCompare(b.feature)
      : a.companyId.localeCompare(b.companyId),
  );
}

const OVERAGE_LABELS: Record<FiduciaireUsageFeature, Record<Locale, string>> = {
  payslips: {
    fr: 'Fiches de salaire supplémentaires',
    de: 'Zusätzliche Lohnabrechnungen',
    en: 'Additional payslips',
    it: 'Buste paga aggiuntive',
  },
  supplierBillExtractions: {
    fr: 'Extractions de justificatifs supplémentaires',
    de: 'Zusätzliche Belegextraktionen',
    en: 'Additional supplier bill extractions',
    it: 'Estrazioni di giustificativi aggiuntive',
  },
  aiPrefills: {
    fr: 'Pré-remplissages IA supplémentaires',
    de: 'Zusätzliche KI-Vorausfüllungen',
    en: 'Additional AI prefills',
    it: 'Precompilazioni IA aggiuntive',
  },
  aiCategorizations: {
    fr: 'Catégorisations IA supplémentaires',
    de: 'Zusätzliche KI-Kategorisierungen',
    en: 'Additional AI categorizations',
    it: 'Categorizzazioni IA aggiuntive',
  },
};

/**
 * Invoice-line text the trustee can reconcile per mandate, e.g.
 * "Fiches de salaire supplémentaires — Mandat A · 09/2025 : 14 × CHF 2.00".
 */
export function overageLineDescription(
  line: Pick<OverageLine, 'feature' | 'overCount' | 'amountChf'>,
  companyName: string,
  monthKey: string,
  locale: Locale,
): string {
  const [year, month] = monthKey.split('-');
  const unitChf = line.amountChf / line.overCount;
  return (
    `${OVERAGE_LABELS[line.feature][locale]} — ${companyName}` +
    ` · ${month}/${year} : ${line.overCount} × CHF ${unitChf.toFixed(2)}`
  );
}
